// ─── Hero: Cinematic ──────────────────────────────────────────
// Full-viewport hero with background image, dark overlay,
// and bottom-aligned staggered text. Editorial feel.

import React from 'react';
import { ArrowRight } from 'lucide-react';
import { AnimatedSection, StaggerContainer } from '../../../lib/animations/AnimatedSection';

export default function HeroCinematic({ section, data, theme, settings, rsvpRef }) {
  const { title, subtitle, dateText, backgroundImage, introLabel, ctaText, ctaTarget } = data;
  const anim = section.animation || {};

  const scrollToTarget = () => {
    if (ctaTarget && ctaTarget !== 'rsvp') {
      const el = document.querySelector(`[id^="${ctaTarget}-"]`) || document.querySelector(`[data-section-type="${ctaTarget}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    rsvpRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Background image with overlay */}
      <AnimatedSection preset={anim.image || 'zoom-soft'} className="absolute inset-0">
        {backgroundImage ? (
          <div
            className="h-full w-full bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(60,40,20,0.20), rgba(60,40,20,0.28)), url(${backgroundImage})`,
            }}
          />
        ) : (
          <div className="h-full w-full" style={{ background: theme.accentSoft }} />
        )}
      </AnimatedSection>

      {/* Content – bottom-aligned */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-end px-6 py-14 sm:px-8 sm:py-20">
        <div className="max-w-4xl">
          <StaggerContainer preset={anim.title || 'stagger-text'}>
            <p
              className="text-[11px] uppercase tracking-[0.22em]"
              style={{ color: 'rgba(255,255,255,0.85)', fontFamily: theme.eyebrowFont }}
            >
              {introLabel || 'We\'re getting married'}
            </p>
            <h1
              className="mt-4 text-5xl leading-[0.92] sm:text-6xl md:text-[6.5rem]"
              style={{ color: '#FFFFFF', fontFamily: theme.headingFont }}
            >
              {title || 'Our Wedding'}
            </h1>
          </StaggerContainer>

          <AnimatedSection preset={anim.subtitle || 'fade-up'} delay={0.3}>
            <p className="mt-6 max-w-2xl text-base leading-7 sm:text-lg" style={{ color: 'rgba(255,255,255,0.90)' }}>
              {subtitle}
            </p>
          </AnimatedSection>

          <AnimatedSection preset='fade-up' delay={0.5}>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.85)' }}>
              <span style={{ fontFamily: theme.eyebrowFont }}>{dateText}</span>
              {ctaText && (
              <button
                onClick={scrollToTarget}
                className="inline-flex items-center gap-2 rounded-full border px-5 py-3 normal-case tracking-normal transition hover:bg-white/10"
                style={{ borderColor: 'rgba(255,255,255,0.30)', color: '#FFFFFF' }}
                data-testid="hero-rsvp-cta"
              >
                {ctaText}
                <ArrowRight className="h-4 w-4" />
              </button>
              )}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
